import { validate as emailValidator } from "email-validator";
import { parseDate, yearsAgo } from "../../../utils/helpers";
import { State, profileType_Fiscal } from "../../../state-actions-reducers";
import { allFiscalCitizenshipCountries } from "../../../content/countries";
import { PassportType } from "../../../enums/passportTypeEnum";
import { SoleTraderType } from "../../../enums/fiscalTypeEnum";
import { Option } from "../../../types/common-types";
import { validatePhone } from "../../validators";

interface VlidateDateArgs {
  current: Date | string;
  min?: Date;
  max?: Date;
  required?: boolean;
}

export function validateDate({ current, min, max, required }: VlidateDateArgs): boolean {
  // By default allow empty strings. Make stronger checking on submitting
  const currentValue = parseDate(current);
  if (!currentValue) {
    return !required;
  }
  if (min && max) {
    return min <= currentValue && currentValue <= max;
  }
  if (min) {
    return min <= currentValue;
  }
  if (max) {
    return currentValue <= max;
  }
  return true;
}

export function smartValidateString(string: string): boolean {
  const trimmedString = (string??'').trim();
  const letter = trimmedString.match(/[a-zA-Zа-яёА-ЯЁ]/g)?.length ?? 0;
  const nums = trimmedString.match(/[0-9]/g)?.length ?? 0;
  const symbols = trimmedString.match(/[\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\']/g)?.length ?? 0;
  return /[a-zA-Zа-яёА-ЯЁ]/g.test(trimmedString) && symbols <= letter * 2 && nums <= letter * 4;
}

export function validateDatesRange(from: Date | string, to: Date | string) {
  const smallerDate = parseDate(from);
  const biggerDate = parseDate(to);
  if (!smallerDate || !biggerDate) {
    return false;
  }
  return smallerDate < biggerDate;
}

export function validateCollegeDates(startDate: string | Date, endDate: string | Date, required = false) {
  if (!startDate || !endDate) return !required;
  // Check if dates is valid
  return validateDatesRange(startDate, endDate);
}

// By default allow empty strings
// Will make stronger validation on submitting
export function validateString(string: string, maxLength: number, required?: boolean, regexp?: RegExp): boolean {
  const minLength = required ? 1 : 0;
  const trimmedString = (string??'').trim();
  return trimmedString.length >= minLength && trimmedString.length <= maxLength && Boolean(trimmedString.match(regexp || ""));
}

export function validateAddress(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function validateBankAccount(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function validateName(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function birthPlace(place: string, required?: boolean): boolean {
  return validateString(place, 300, required);
}

export function stringDigitsLength(digits: string, digitslength: number, required = true): boolean {
  return validateString(digits, 100, false) && ((required && digits.length == digitslength) || !required);
}

export function passportDigits(digits: string, required?: boolean): boolean {
  return validateString(digits, 100, required);
}

export function passportText(text: string, required?: boolean): boolean {
  return validateString(text, 400, required);
}

export function validateEmail(text: string, required?: boolean): boolean {
  if (!text && !required) return true;
  return validateString(text, 100, required) && emailValidator(text);
}

export function validateReferences(values: string[], required = false): boolean {
  if (!required && values.length === 0) return true;
  if (required && values.length === 0) return false;
  if (values.length < 20) {
    return values.every((value) => {
      return validateString(value, 200);
    });
  }
  return false;
}

const URL_REGEXP =
  /^(https?:\/\/)?(www\.)?[-a-zа-яё0-9@:%._\+~#=]{2,256}\.[a-zа-яё]{1,4}(?::[0-9]+)?(\/?[-a-zа-яё0-9@:%_\+~#]+(\.[a-zа-яё0-9]+)?)+(\?[-а-яёa-z0-9@:%._\+~#]+(=[-a-zа-яё0-9@:%._\+~#]+)?((\&[-a-zа-яё0-9@:%._\+~#]+(=[-a-zа-яё0-9@:%._\+~#]+)?)+)?)?/gi;
export function validateUrl(url: string, maxLength = 300, required = false): boolean {
  if (!url && !required) return true;
  if (url) {
    if (typeof url === "string") {
      if (url.length < maxLength) {
        return Boolean(url.match(URL_REGEXP));
      }
    }
  }
  return false;
}

export function validateSocialLink(value: string, required = false): boolean {
  return validateUrl(value, 300, required);
  return false;
}

export function validatePassportNumber(value: string, required = false, minLength?: number, maxLength?: number) {
  return validateString(value, maxLength || 10, required) && value.replace(/[^a-zA-Zа-яА-Я0-9 ]/g, "").length >= (minLength || 7);
}

export function validateStepIndividualKzt(step: number, state: State): string[] {
  let selectedCitizenship, countryRegistration;
  allFiscalCitizenshipCountries.map((country: Option) => {
    if (country.value === state.citizenship) selectedCitizenship = country.label.rus;
    if (country.value === state.countryRegistration) countryRegistration = country.label.rus;
  });
  switch (step) {
    case 1:
      let citizenshipFields = [];
      let solarType = [];
      if (Number(state.soleTraderType) === SoleTraderType.OldStyle) {
        solarType.push(
          !validatePassportNumber(state.certificateRegistrationNumber, true, 11, 11) && "certificateRegistrationNumber",
          state.stateRegistrationCertificate.length === 0 && "stateRegistrationCertificate",
          !validateDate({
            current: state.registeredDate,
            required: true,
          }) && "registeredDate"
        );
      }
      if (Number(state.soleTraderType) === SoleTraderType.NewStyle) {
        solarType.push(
          !validatePassportNumber(state.registrationTicket, true, 15, 15) && "registrationTicket",
          !validatePassportNumber(state.registrationNotice, true, 15, 15) && "registrationNotice",
          !validateDate({
            current: state.receptionNoticeDate,
            required: true,
          }) && "receptionNoticeDate",
          !validateDate({
            current: state.registrationNoticeDate,
            min: state.receptionNoticeDate ? new Date(state.receptionNoticeDate) : new Date(2017, 0, 1),
            required: true,
          }) && "registrationNoticeDate",
          state.scanSoleRegistrationTicket.length === 0 && "scanSoleRegistrationTicket",
          state.scanSoleRegistrationTicketSecond.length === 0 && "scanSoleRegistrationTicketSecond",
          state.scanOfNotificationFirst.length === 0 && "scanOfNotificationFirst",
          state.scanOfNotificationSecond.length === 0 && "scanOfNotificationSecond"
        );
      }

      if (selectedCitizenship === "Российская Федерация") {
        if (Number(state.passportType) === PassportType.Passport) {
          citizenshipFields.push(
            !validatePassportNumber(state.passportNumber, true, 11, 11) && "passportNumber",
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportIAt,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportIAt",
            !validatePassportNumber(state.passportAuthorityCode, true, 6, 7) && "passportAuthorityCode",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
            state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
            // !state.passportDateBoardCheck && 'passportDateBoardCheck'
          );
        }
        if (Number(state.passportType) === PassportType.InternationalPassport) {
          citizenshipFields.push(
            !validatePassportNumber(state.passportInternationalNumber, true, 10, 10) && "passportInternationalNumber",
            !validateDate({
              current: state.passportExpiryDate,
              min: new Date(),
              max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
              required: true,
            }) && "passportExpiryDate",
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportIAt,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportIAt",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport"
          );
        }
        if (Number(state.passportType) === PassportType.ResidencePermit) {
          citizenshipFields.push(
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
            state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
          );
        }
        if (Number(state.passportType) === PassportType.TempResidencePermit) {
          citizenshipFields.push(
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            !validateDate({
              current: state.passportExpiryDate,
              min: new Date(),
              max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
              required: true,
            }) && "passportExpiryDate",
            state.residenceImageFile.length === 0 && "residenceImageFile"
          );
        }
      }
      if (selectedCitizenship === "Беларусь") {
        if (Number(state.passportType) === PassportType.ResidencePermit) {
          citizenshipFields.push(
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
            state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
          );
        }
      }
      if (selectedCitizenship === "Кыргызстан") {
        if (Number(state.passportType) === PassportType.Passport) {
          citizenshipFields.push(
            !validatePassportNumber(state.passportNumber, true, 10, 10) && "passportNumber",
            !validateDate({
              current: state.passportExpiryDate,
              min: new Date(),
              max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
              required: true,
            }) && "passportExpiryDate",
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportIAt,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportIAt",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport"
            // !state.passportDateBoardCheck && 'passportDateBoardCheck'
          );
        }
        if (Number(state.passportType) === PassportType.ResidencePermit) {
          citizenshipFields.push(
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
            state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
          );
        }
        if (Number(state.passportType) === PassportType.TempResidencePermit) {
          citizenshipFields.push(
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            !validateDate({
              current: state.passportExpiryDate,
              min: new Date(),
              max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
              required: true,
            }) && "passportExpiryDate",
            state.residenceImageFile.length === 0 && "residenceImageFile"
          );
        }
        if (Number(state.passportType) === PassportType.IdCard) {
          citizenshipFields.push(
            !validatePassportNumber(state.passportCardNumber, true, 10, 10) && "passportCardNumber",
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportIAt,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportIAt",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport"
            // !state.passportDateBoardCheck && 'passportDateBoardCheck'
          );
        }
      }
      if (selectedCitizenship === "Румыния") {
        citizenshipFields.push(
          !validateString(state.passportCardNumber, 30, true) && "passportCardNumber",
          !validateDate({
            current: state.passportExpiryDate,
            min: new Date(),
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
            required: true,
          }) && "passportExpiryDate",
          state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
          state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
        );
      }
      if (selectedCitizenship === "Китай") {
        citizenshipFields.push(
          !validatePassportNumber(state.passportNumber, true, 10, 11) && "passportNumber",
          !validateString(state.passportCardNumber, 30, true) && "passportCardNumber",
          !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
          !validateDate({
            current: state.passportIAt,
            min: yearsAgo(130),
            max: new Date(),
            required: true,
          }) && "passportIAt",
          !validatePassportNumber(state.passportAuthorityCode, true, 6, 7) && "passportAuthorityCode",
          !validateDate({
            current: state.passportExpiryDate,
            min: new Date(),
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
            required: true,
          }) && "passportExpiryDate",
          state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
          state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
        );
      }
      if (selectedCitizenship === "Казахстан") {
        citizenshipFields.push(
          !validatePassportNumber(state.passportCardNumber, true, 9, 9) && "passportCardNumber",
          !validateDate({
            current: state.passportIAt,
            min: yearsAgo(130),
            max: new Date(),
            required: true,
          }) && "passportIAt",
          !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
          !validateDate({
            current: state.passportExpiryDate,
            min: new Date(new Date().setDate(new Date().getDate() - 1)),
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
            required: true,
          }) && "passportExpiryDate",
          state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
          state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
        );
      }
      if (selectedCitizenship === "Бразилия" || selectedCitizenship === "Италия") {
        citizenshipFields.push(
          !validatePassportNumber(state.passportNumber, true, 10, 11) && "passportNumber",
          !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
          !validateDate({
            current: state.passportIAt,
            min: yearsAgo(130),
            max: new Date(),
            required: true,
          }) && "passportIAt",
          state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
          state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
        );
      }
      if (
        selectedCitizenship === "Австралия" ||
        selectedCitizenship === "Азербайджан" ||
        selectedCitizenship === "Армения" ||
        selectedCitizenship === "Соединенное Королевство" ||
        selectedCitizenship === "Испания" ||
        selectedCitizenship === "Сирийская Арабская Республика" ||
        selectedCitizenship === "Узбекистан" ||
        selectedCitizenship === "Франция" ||
        selectedCitizenship === "Швейцария"
      ) {
        if (Number(state.passportType) === PassportType.InternationalPassport) {
          citizenshipFields.push(
            !validatePassportNumber(state.passportInternationalNumber, true, 9, 10) && "passportInternationalNumber",
            !validateDate({
              current: state.passportExpiryDate,
              min: new Date(),
              max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
              required: true,
            }) && "passportExpiryDate",
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportIAt,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportIAt",
            state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport"
            // !state.passportDateBoardCheck && 'passportDateBoardCheck'
          );
        }
        if (Number(state.passportType) === PassportType.ResidencePermit) {
          citizenshipFields.push(
            !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
            !validateDate({
              current: state.passportDateIssuePermanentResidency,
              min: yearsAgo(130),
              max: new Date(),
              required: true,
            }) && "passportDateIssuePermanentResidency",
            state.residenceImageFile.length === 0 && "residenceImageFile",
            state.addressImageFile.length === 0 && "addressImageFile"
          );
        }
      }
      if (
        selectedCitizenship === "Болгария" ||
        selectedCitizenship === "Израиль" ||
        selectedCitizenship === "Исландия" ||
        selectedCitizenship === "Молдова, Республика" ||
        selectedCitizenship === "Нидерланды" ||
        selectedCitizenship === "Норвегия" ||
        selectedCitizenship === "Швеция" ||
        selectedCitizenship === "Южная Африка" ||
        selectedCitizenship === "Вьетнам" ||
        selectedCitizenship === "Грузия" ||
        selectedCitizenship === "Индонезия" ||
        selectedCitizenship === "Индия" ||
        selectedCitizenship === "Южная Корея" ||
        selectedCitizenship === "Финляндия"
      ) {
        citizenshipFields.push(
          !validatePassportNumber(state.passportNumber, true, 10, 11) && "passportNumber",
          citizenshipFields.push(!validateString(state.passportPersonNumber, 30, true) && "passportPersonNumber"),
          !smartValidateString(state.passportIssuedBy) && "passportIssuedBy",
          !validateDate({
            current: state.passportIAt,
            min: yearsAgo(130),
            max: new Date(),
            required: true,
          }) && "passportIAt",
          !validateDate({
            current: state.passportExpiryDate,
            min: new Date(),
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
            required: true,
          }) && "passportExpiryDate",
          state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
          state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address"
        );
      }
      return [
        // // Array of invalid field names
        // // validate(): boolean && 'field name' -> null or 'field name'
        // // Then this array filtered with the Boolean()
        // !validateString(state.mainCity, 100, true) && 'mainCity',
        !validateName(state.F_FiscalType, true) && "F_FiscalType",
        !smartValidateString(state.headFirstname) && "headFirstname",
        !smartValidateString(state.headLastname) && "headLastname",
        !smartValidateString(state.headPatronymic) && !state.noPatronymic && "headPatronymic",
        !smartValidateString(state.headFirstnameGenitive) && "headFirstnameGenitive",
        !smartValidateString(state.headLastnameGenitive) && "headLastnameGenitive",
        !smartValidateString(state.headPatronymicGenitive) && !state.noPatronymic && !state.noPatronymic && "headPatronymicGenitive",
        !stringDigitsLength(state.F_INN, 12) && "F_INN",
        !validateString(state.addressFactualCountry, 300, true) && "addressFactualCountry",
        !validateString(state.addressFactualRegion, 300, true) && "addressFactualRegion",
        !validateString(state.addressFactualCity, 300, true) && "addressFactualCity",
        !smartValidateString(state.addressFactualStreet) && "addressFactualStreet",
        !validateString(state.addressFactualBuilding, 300, true) && "addressFactualBuilding",
        // !validateString(state.addressFactualApartments, 300, true) && !state.addressFactualNoApartments && "addressFactualApartments",
        !validateString(state.addressFactualOffice, 300, true) && !state.addressFactualNoApartments && "addressFactualOffice",
        state.addressRegisteredPostCode.length < 6 && "addressRegisteredPostCode",
        !validateString(state.addressRegisteredCountry, 300, true) && "addressRegisteredCountry",
        !validateString(state.addressRegisteredRegion, 300, true) && "addressRegisteredRegion",
        !validateString(state.addressRegisteredCity, 300, true) && "addressRegisteredCity",
        !smartValidateString(state.addressRegisteredStreet) && "addressRegisteredStreet",
        !validateString(state.addressRegisteredBuilding, 300, true) && "addressRegisteredBuilding",
        !validateString(state.addressRegisteredApartments, 300, true) &&
          !state.addressRegisteredNoApartments &&
          'addressRegisteredApartments',
        // !validateString(state.addressRegisteredOffice, 300, true) && !state.addressRegisteredNoApartments && "addressRegisteredOffice",
        state.addressFactualPostCode.length < 6 && "addressFactualPostCode",
        !validateDate({
          current: state.birthDate,
          min: yearsAgo(130),
          max: yearsAgo(14),
          required: true,
        }) && "birthDate",
        !smartValidateString(state.birthPlace) && "birthPlace",
        !validateString(state.F_FullName, 1000, true) && "F_FullName",
        ...solarType,
        // !validateString(state.sex, 10, true) && 'sex',
        ...citizenshipFields,
        state.oKED.replace(/_/gim, "").length > 0 && state.oKED.replace(/_/gim, "").length < 5 && "oKED",
        state.F_KBe.replace(/_/gim, "").length > 0 && state.F_KBe.replace(/_/gim, "").length < 2 && "F_KBe",
      ].filter(Boolean) as string[];
    case 2:
      return [
        ...state.BankDetails.map((bank, i) => {
          if (bank.F_BankRS.length < 12) return `F_BankRS${i}`;
        }),
        ...state.BankDetails.map((bank, i) => {
          if (bank.F_BankBIK.length < 8) return `F_BankBIK${i}`;
        }),
        ...state.BankDetails.map((bank, i) => {
          if (!smartValidateString(bank.F_BankName)) return `F_BankName${i}`;
        }),
      ].filter(Boolean) as string[];
    case 3:
      return [
        state?.telegram[0]?.replace(/[^A-Za-z0-9@_]/g, "").length > 0 &&
          (state?.telegram[0]?.replace(/[^A-Za-z0-9_]/g, "").length < 5 || (state?.telegram[0]?.match(/[a-zA-Zа-яёА-ЯЁ]/g) || "")?.length < 1) &&
          "telegram",
        !state.contactPhones.some((number) => validatePhone(number)) && "contactPhones",
        !state?.contactEmails?.some((contactEmails) => validateEmail(contactEmails, true)) && "contactEmails",
        state?.personalWebsite[0]?.length > 0 && !validateUrl(state?.personalWebsite[0], 300, true) && "personalWebsite",
        !smartValidateString(state.purposeOfLease) && "purposeOfLease",
        !state.additionalLinksDescription.some((desc) => validateString(desc, 300, true)) && "additionalLinksDescription",
        !state.additionalLinks.some((links) => validateString(links, 200, true)) && "additionalLinks",
        !smartValidateString(state.contactPersonFirstname) && "contactPersonFirstname",
        !smartValidateString(state.contactPersonLastname) && "contactPersonLastname",
        !smartValidateString(state.contactPersonPatronymic) && !state.noPatronymicContact && "contactPersonPatronymic",
        !state.contactPersonPhones.some((number) => validatePhone(number)) && "contactPersonPhones",
        state?.contactPhoneSeconds?.[0]?.length > 0 && !state?.contactPhoneSeconds?.some((number) => validatePhone(number)) && "contactPhoneSeconds",
        !state.contactPersonEmails.some((contactPersonEmails) => validateEmail(contactPersonEmails, true)) && "contactPersonEmails",
        !smartValidateString(state.contactPersonPosition) && "contactPersonPosition",
      ].filter(Boolean) as string[];
    case 4:

    default:
      return [];
  }
}

export function validateForm(state: State): string[] {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce((result: string[], step: number): string[] => [...result, ...validateStepIndividualKzt(step, state)], []);
}

export function trimForm(state: State): State {
  return Object.keys(state).reduce((trimmedState: State, key) => {
    switch (key) {
      case "mobilePhoneNumbers":
      case "F_WorkPhoneNumber":
      case "contactPhones":
      case "addressRegisteredPhones":
      case "addressFactualPhones":
      case "workPhoneNumbers":
        // Filter out invalid values
        return {
          ...trimmedState,
          [key]: state[key].filter(validatePhone),
        };
      case "emails":
        return {
          ...trimmedState,
          emails: state.emails.filter((email) => validateEmail(email, true)),
        };
      case "F_Email":
        return {
          ...trimmedState,
          emails: state.emails.filter((email) => validateEmail(email, true)),
        };
      case "vk":
      case "facebook":
      case "youtube":
      case "vimeo":
      case "instagram":
      case "workWebsite":
      case "personalWebsite":
      case "additionalLinks":
      case "addressRegisteredDate":
        return {
          ...trimmedState,
          addressRegisteredDate: validateDate({
            current: state.addressRegisteredDate,
            max: new Date(),
          })
            ? state.addressRegisteredDate
            : "",
        };
      case "isStudent":
        return {
          ...trimmedState,
          collegeStartedAt: state.isStudent ? state.collegeStartedAt : "",
          collegeEndedAt: state.isStudent ? state.collegeEndedAt : "",
        };
      default:
        return trimmedState;
    }
  }, state);
}
